import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

const SPOTLIGHT_PADDING = 6;
const TOOLTIP_GAP = 12;
const VIEWPORT_MARGIN = 16;
const DEFAULT_TOOLTIP_WIDTH = 340;

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

// Resolves a CSS selector to a viewport rect, polling briefly and re-measuring
// on scroll/resize, and again when remeasureKey changes (the step, the page).
// Returns null if the element never appears within the budget.
export const useTargetRect = (selector, enabled, remeasureKey = '') => {
  const [rect, setRect] = useState(null);

  const measure = useCallback(() => {
    if (!enabled || !selector) {
      setRect(null);
      return;
    }
    const el = document.querySelector(selector);
    if (!el) {
      setRect((prev) => (prev === null ? prev : null));
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [selector, enabled]);

  useLayoutEffect(() => {
    if (!enabled || !selector) return undefined;
    let tries = 0;
    measure();
    const interval = setInterval(() => {
      measure();
      tries += 1;
      if (document.querySelector(selector) || tries > 60) clearInterval(interval);
    }, 80);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [selector, enabled, measure, remeasureKey]);

  return rect;
};

// Position the tooltip relative to the target rect, clamped to viewport.
// If rect is null we either center the tooltip (when dim) or anchor it to the
// bottom-right corner (when undimmed — floating tip pattern from Linear/Notion).
const computeTooltipPosition = (rect, placement, tooltipWidth, tooltipHeight, dim) => {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;

  if (!rect) {
    if (!dim) {
      // Floating tip — bottom-right so it doesn't obscure the page content.
      return {
        top: vh - tooltipHeight - VIEWPORT_MARGIN,
        left: vw - tooltipWidth - VIEWPORT_MARGIN,
        centered: false,
      };
    }
    return {
      top: Math.max(VIEWPORT_MARGIN, vh / 2 - tooltipHeight / 2),
      left: Math.max(VIEWPORT_MARGIN, vw / 2 - tooltipWidth / 2),
      centered: true,
    };
  }

  const order = placement === 'auto'
    ? ['bottom', 'right', 'top', 'left']
    : [placement, 'bottom', 'right', 'top', 'left'];

  for (const p of order) {
    let top;
    let left;
    if (p === 'bottom') {
      top = rect.top + rect.height + TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
    } else if (p === 'top') {
      top = rect.top - tooltipHeight - TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
    } else if (p === 'right') {
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.left + rect.width + TOOLTIP_GAP;
    } else if (p === 'left') {
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.left - tooltipWidth - TOOLTIP_GAP;
    }
    const fits =
      top >= VIEWPORT_MARGIN &&
      top + tooltipHeight <= vh - VIEWPORT_MARGIN &&
      left >= VIEWPORT_MARGIN &&
      left + tooltipWidth <= vw - VIEWPORT_MARGIN;
    if (fits) {
      return { top, left, centered: false };
    }
  }

  // Nothing fit cleanly — clamp the preferred placement into the viewport.
  let top = rect.top + rect.height + TOOLTIP_GAP;
  let left = rect.left + rect.width / 2 - tooltipWidth / 2;
  top = clamp(top, VIEWPORT_MARGIN, vh - tooltipHeight - VIEWPORT_MARGIN);
  left = clamp(left, VIEWPORT_MARGIN, vw - tooltipWidth - VIEWPORT_MARGIN);
  return { top, left, centered: false };
};

/**
 * Full-screen overlay that dims the page and cuts a spotlight around a target
 * rectangle, with a tooltip positioned next to it. Tooltip content is supplied
 * via `children`. If no rect, the overlay is uniformly dim and the tooltip is
 * centered (graceful fallback for concept-only steps).
 */
const SpotlightOverlay = ({
  rect,
  placement = 'bottom',
  tooltipWidth = DEFAULT_TOOLTIP_WIDTH,
  onDismiss,
  dismissable = true,
  // When `dim` is false and there's no target rect, the overlay renders only
  // the tooltip as a floating tip in the bottom-right corner without dimming
  // the page. Useful for concept-only tutorial steps where the user benefits
  // from seeing the actual page (e.g. Documents list, Habits empty state).
  dim = true,
  children,
}) => {
  const { t } = useTranslation();
  // Tooltip height isn't known until after first paint, and it changes again on
  // every step transition (different copy + optional visual). A ResizeObserver
  // keeps `measuredHeight` in sync whenever the card's content resizes so the
  // placement math never positions a now-taller card off the bottom of the
  // viewport. (BG_89/BG_92: the previous ref-callback measured once on mount and
  // went stale across steps, clipping the footer nav buttons out of view.)
  const [measuredHeight, setMeasuredHeight] = useState(180);
  const tooltipRef = useRef(null);
  useLayoutEffect(() => {
    const node = tooltipRef.current;
    if (!node) return undefined;
    const measure = () => {
      const h = node.getBoundingClientRect().height;
      if (h) setMeasuredHeight((prev) => (Math.abs(h - prev) > 2 ? h : prev));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  // Targets that occupy more than half the viewport can't be highlighted
  // meaningfully: dimming "everything else" dims almost nothing, and the
  // tooltip can't find a placement that doesn't clip. Fall back to floating
  // tip — the user is already routed to the page, so they see the feature.
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
  const isTargetHuge =
    rect && rect.width * rect.height > vw * vh * 0.5;
  const effectiveRect = isTargetHuge ? null : rect;

  const pos = computeTooltipPosition(effectiveRect, placement, tooltipWidth, measuredHeight, dim);
  const showDimLayer = !isTargetHuge && (dim || !!effectiveRect);

  // Cutout geometry. We pick the corner radius based on the target's aspect
  // ratio: roughly-square elements (icons, icon buttons) get a true circle,
  // wider/taller elements (cards, sidebars, content blocks) get a clean
  // rounded rectangle. Using min(w,h)/2 uniformly turned a 300x1400 sidebar
  // into a pill-shaped balloon — Driver.js / Shepherd never do that.
  const cutoutX = effectiveRect ? effectiveRect.left - SPOTLIGHT_PADDING : 0;
  const cutoutY = effectiveRect ? effectiveRect.top - SPOTLIGHT_PADDING : 0;
  const cutoutW = effectiveRect ? effectiveRect.width + SPOTLIGHT_PADDING * 2 : 0;
  const cutoutH = effectiveRect ? effectiveRect.height + SPOTLIGHT_PADDING * 2 : 0;
  let cutoutR = 0;
  if (effectiveRect) {
    const aspect = cutoutW / cutoutH;
    const squarish = aspect > 0.85 && aspect < 1.18;
    cutoutR = squarish
      ? Math.min(cutoutW, cutoutH) / 2
      : Math.min(10, Math.min(cutoutW, cutoutH) / 4);
  }

  // Esc closes the overlay if dismissable.
  useEffect(() => {
    if (!dismissable) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onDismiss?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dismissable, onDismiss]);

  if (typeof document === 'undefined') return null;

  // When no target and no dim, the container is pointer-events-none so the
  // underlying page stays fully interactive. The tooltip explicitly re-enables
  // pointer events on itself so its buttons still work.
  const containerInteractive = showDimLayer;

  const overlay = (
    <div
      className={`fixed inset-0 z-[100] ${containerInteractive ? '' : 'pointer-events-none'}`}
      aria-modal="true"
      role="dialog"
    >
      {showDimLayer && (
        <svg
          className="absolute inset-0 h-full w-full pointer-events-none"
          aria-hidden="true"
        >
          <defs>
            <mask id="spotlight-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {effectiveRect && (
                <rect
                  x={cutoutX}
                  y={cutoutY}
                  width={cutoutW}
                  height={cutoutH}
                  rx={cutoutR}
                  ry={cutoutR}
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0,0,0,0.65)"
            mask="url(#spotlight-mask)"
          />
          {effectiveRect && (
            <rect
              x={cutoutX - 2}
              y={cutoutY - 2}
              width={cutoutW + 4}
              height={cutoutH + 4}
              rx={cutoutR + 2}
              ry={cutoutR + 2}
              fill="none"
              stroke="rgba(245, 158, 11, 0.9)"
              strokeWidth="2"
            />
          )}
        </svg>
      )}

      {/* Click-anywhere-to-advance scrim behind the tooltip. We don't want this
          to catch clicks on the tooltip itself, so the tooltip sits above on z. */}
      {dismissable && showDimLayer && (
        <button
          type="button"
          aria-label={t('ui_views.tutorials.dismiss')}
          onClick={onDismiss}
          className="absolute inset-0 cursor-default bg-transparent"
          style={{ zIndex: 0 }}
        />
      )}

      <div
        ref={tooltipRef}
        className="pointer-events-auto absolute overflow-y-auto rounded-lg border border-border bg-popover shadow-xl"
        style={{
          top: pos.top,
          left: pos.left,
          width: tooltipWidth,
          // Safety net: if a step's content is taller than the viewport (small
          // screens / long translations), cap the card and let it scroll instead
          // of clipping its footer controls out of view.
          maxHeight: vh - VIEWPORT_MARGIN * 2,
          zIndex: 1,
        }}
      >
        {children}
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

export default SpotlightOverlay;
